import styled, { css } from "styled-components";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin, fontWeights } from "../../../design-system/tokens/typography";
import { borderRadius } from "../../../design-system/tokens/borders";
import { rawColors } from "../../../design-system/tokens/colors";

// GAP: local spacing tokens have no 14px step (F18 message card padding).
const CHAT_CARD_PADDING = "0.875rem";

export const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  min-width: 0;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  overflow-y: auto;
`;

export const PreviewLabel = styled.span`
  ${typographyMixin("caption", 3)}
  font-weight: ${fontWeights.bold};
  color: ${rawColors.text.highEmphasis};
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  padding: ${spacing[5]};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.xl};
  background-color: ${rawColors.primary[50]};
    // flex: 1 1 0;
  // min-height: 0;
  // overflow: hidden;

`;

export const InnerCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  padding: ${spacing[4]};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  background-color: ${rawColors.structural.white};
  
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

export const LogoImg = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
`;

export const BrandName = styled.span`
  ${typographyMixin("subtitle", 3)}
  color: ${rawColors.text.highEmphasis};
`;

export const Body = styled.p`
  margin: 0;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
`;

/** Same column widths + gutter as the preview panels so CTAs line up with cards above. */
const previewPanelTwoColumn = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing[3]};
  align-items: stretch;
  width: 100%;
  min-width: 0;
`;

export const Grid = styled.div`
  ${previewPanelTwoColumn}
`;

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  padding: ${spacing[3]};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  background-color: ${rawColors.structural.white};
`;

export const SummaryPanel = styled(Panel)`
  background-color: ${rawColors.structural[100]};
`;

export const ChartPreview = styled.div`
  width: 100%;
  aspect-ratio: 1876 / 1088;
  border-radius: ${borderRadius.md};
  overflow: hidden;
  border: 1px solid ${rawColors.stroke[200]};
  background-color: ${rawColors.structural.white};
`;

export const ChartPreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top left;
  display: block;
`;

export const PanelFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const PanelTag = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
`;

export const SummaryTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  ${typographyMixin("body", 2)}
  color: ${rawColors.text.highEmphasis};
`;

export const BulletList = styled.ul`
  margin: 0;
  padding-left: ${spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
`;

export const Bullet = styled.li`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.highEmphasis};
`;

/**
 * Email preview — primary CTA on its own full-width row, the two secondary
 * CTAs split 50/50 beneath it. Full width keeps every label on one line at
 * any pane width and the primary button at its natural height.
 */
export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 100%;
  min-width: 0;
`;

export const SecondaryButtonPair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing[2]};
  min-width: 0;
  width: 100%;
`;

export const PrimaryCta = styled.button`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: ${spacing[2]} ${spacing[3]};
  text-align: center;
  background-color: ${rawColors.primary[500]};
  color: ${rawColors.text.white};
  border: none;
  border-radius: ${borderRadius.md};
  ${typographyMixin("body", 2)}
  cursor: pointer;
`;

export const SecondaryCta = styled.button`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: ${spacing[2]} ${spacing[3]};
  text-align: center;
  white-space: nowrap;
  background-color: ${rawColors.structural.white};
  color: ${rawColors.text.highEmphasis};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.md};
  ${typographyMixin("body", 3)}
  cursor: pointer;
`;

/* ---------------------------------------------------------------------------
 * Slack / Teams chat-style preview (vertical, no outer tinted frame)
 * ------------------------------------------------------------------------- */

export const ChatCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  padding: ${CHAT_CARD_PADDING};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius[14]};
  background-color: ${rawColors.structural.white};
`;

export const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

export const LogoPillWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[1]};
  border-radius: ${borderRadius.sm};
  background-color: ${rawColors.primary[100]};
`;

export const LogoPillImg = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

export const ChatBrandName = styled.span`
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
`;

export const MessageText = styled.p`
  margin: 0;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.highEmphasis};
`;

/**
 * Slack indents threaded content under the author row. The 2.25rem inset
 * (≈36px at default root font size) matches Figma's "pl-[36px]" spacing.
 */
export const IndentedBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  position: relative;
`;

/**
 * Slack thread indicator — a rounded pill that sits just inside the indent,
 * visually tying the message + attachment together.
 */
export const ThreadBar = styled.span`
  display: none; /* F18 message card has no thread rail */
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${spacing[2]};
  width: 3px;
  border-radius: 999px;
  background-color: ${rawColors.stroke[200]};
`;

export const AttachmentCard = styled.div`
  display: inline-flex;
  flex-direction: column;
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  overflow: hidden;
  width: 100%;
  background-color: ${rawColors.structural.white};
`;

export const AttachmentImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  // object-fit: cover;
`;

export const AttachmentFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing[2]} ${spacing[3]};
  border-top: 1px solid ${rawColors.stroke[200]};
`;

export const AttachmentName = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.highEmphasis};
`;

export const AttachmentMeta = styled.span`
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.lowEmphasis};
`;

/** Slack — same outer two-column layout as email preview. */
export const ChatActions = styled.div`
  ${previewPanelTwoColumn}
`;

/** Teams (single CTA) — full-width row, not the 3-column grid. */
export const SingleChatActionRow = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
`;

export const ChatPrimaryCta = styled.button`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  width: 100%;
  min-width: 0;
  padding: ${spacing[2]} ${spacing[4]};
  background-color: ${rawColors.primary[500]};
  color: ${rawColors.text.white};
  border: none;
  border-radius: ${borderRadius.lg};
  ${typographyMixin("caption", 3)}
  cursor: pointer;
`;

/** Info note below the attachment — e.g. "invite the bot" hint for Slack.
 * Sits in the white space below the attachment card. */
export const InfoHintRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  color: ${rawColors.text.lowEmphasis};
`;

export const InfoHintText = styled.p`
  margin: 0;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.lowEmphasis};
`;

export const ChatSecondaryCta = styled.button`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 0;
  padding: ${spacing[2]} ${spacing[4]};
  background-color: ${rawColors.structural.white};
  color: ${rawColors.text.mediumEmphasis};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  ${typographyMixin("caption", 3)}
  cursor: pointer;
`;

/**
 * Stands in for the attachment's front page while the deck renders or when
 * the render failed — same 16:9 footprint, so the card doesn't jump when the
 * page image arrives.
 */
export const AttachmentStatus = styled.div<{ $tone?: "muted" | "error" }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  width: 100%;
  aspect-ratio: 16 / 9;
  padding: ${spacing[3]};
  box-sizing: border-box;
  text-align: center;
  background-color: ${rawColors.grey[50]};
  ${typographyMixin("caption", 3)}
  color: ${({ $tone }) =>
    $tone === "error" ? rawColors.accent.red500 : rawColors.text.mediumEmphasis};
`;
